/**
 * Diccionario de errores para el módulo de autenticación
 * Mapea códigos de error a mensajes amigables para el usuario
 */

export const ERROR_CODES = {
  BLOCKED_ACCOUNT: '100002_USAPLOG_BLQ',
  INVALID_CREDENTIALS: '100008_USAPLOG_CRIN',
  INVALID_CREDENTIALS_ALT: 'INVALID_CREDENTIALS',
  EXPIRED_PASSWORD: 'EXPIRED_PASSWORD',
  BLOCKED_ACCOUNT_TEMP: 'BLOCKED_ACCOUNT_TEMP',
} as const

export const ERROR_MESSAGES: Record<string, string> = {
  [ERROR_CODES.BLOCKED_ACCOUNT]: 'Tu cuenta no esta disponible, contacta al administrador.',
  [ERROR_CODES.INVALID_CREDENTIALS]: 'Correo y/o contraseña inválida.',
  [ERROR_CODES.BLOCKED_ACCOUNT_TEMP]: 'Tu cuenta ha sido bloqueada debido a múltiples intentos fallidos. Contacta al administrador.',
  [ERROR_CODES.EXPIRED_PASSWORD]:
    'Hemos enviado un correo con un enlace al que debes acceder para cambiar tu contraseña.',
  DEFAULT: 'Ha ocurrido un error. Por favor, intenta nuevamente.',
}

/**
 * Extrae el número de intentos restantes del mensaje de error
 */
const extractAttemptsRemaining = (errorMessage: string): number | null => {
  // Buscar patrón 100008_USAPLOG_CRIN seguido de un número
  const match = errorMessage.match(/100008_USAPLOG_CRIN\s+(\d+)/)
  return match ? parseInt(match[1], 10) : null
}

/**
 * Verifica si el error es de credenciales inválidas (cualquiera de los dos formatos)
 */
const isInvalidCredentialsError = (errorMessage: string): boolean => {
  return (
    errorMessage.includes(ERROR_CODES.INVALID_CREDENTIALS) ||
    errorMessage.includes(ERROR_CODES.INVALID_CREDENTIALS_ALT) ||
    errorMessage.toLowerCase().includes('invalid') ||
    errorMessage.toLowerCase().includes('credenciales')
  )
}

/**
 * Obtiene el mensaje de error correspondiente al código o mensaje recibido
 */
export const getErrorMessage = (errorCodeOrMessage?: string): string => {
  if (!errorCodeOrMessage) {
    return ERROR_MESSAGES.DEFAULT
  }

  // Manejar error de credenciales inválidas con intentos restantes
  if (isInvalidCredentialsError(errorCodeOrMessage)) {
    // Si contiene el código 100008_USAPLOG_CRIN, intentar extraer el número de intentos
    if (errorCodeOrMessage.includes(ERROR_CODES.INVALID_CREDENTIALS)) {
      const attemptsRemaining = extractAttemptsRemaining(errorCodeOrMessage)
      if (attemptsRemaining !== null) {
        if (attemptsRemaining === 1) {
          return 'Correo y/o contraseña inválida. Te queda 1 intento.'
        } else if (attemptsRemaining === 2) {
          return 'Correo y/o contraseña inválida. Te quedan 2 intentos.'
        } else if (attemptsRemaining === 3) {
          return 'Correo y/o contraseña inválida. Te quedan 3 intentos.'
        }
        return `Correo y/o contraseña inválida. Te quedan ${attemptsRemaining} intentos antes de que tu cuenta sea bloqueada.`
      }
    }
    // Si no tiene número de intentos o es otro formato, retornar mensaje genérico
    return ERROR_MESSAGES[ERROR_CODES.INVALID_CREDENTIALS]
  }

  // Buscar si el mensaje contiene algún código de error conocido
  for (const [code, message] of Object.entries(ERROR_MESSAGES)) {
    if (errorCodeOrMessage.includes(code)) {
      return message
    }
  }

  // Si no se encuentra un código conocido, retornar el mensaje original o el default
  return errorCodeOrMessage || ERROR_MESSAGES.DEFAULT
}

/**
 * Verifica si el error requiere mostrar un modal especial
 */
export const shouldShowModal = (errorMessage?: string): boolean => {
  if (!errorMessage) {
    return false
  }

  return errorMessage.includes(ERROR_CODES.BLOCKED_ACCOUNT)
}

/**
 * Obtiene el título del modal según el tipo de error
 */
export const getErrorModalTitle = (errorCodeOrMessage?: string): string => {
  if (!errorCodeOrMessage) {
    return 'Error'
  }

  // Para errores de cuenta bloqueada, mostrar "Error"
  if (errorCodeOrMessage.includes(ERROR_CODES.BLOCKED_ACCOUNT)) {
    return 'Error'
  }

  // Para errores de contraseña expirada, mostrar "Revisa tu bandeja de entrada"
  if (errorCodeOrMessage.includes(ERROR_CODES.EXPIRED_PASSWORD)) {
    return 'Revisa tu bandeja de entrada'
  }

  // Por defecto, mostrar "Error"
  return 'Error'
}

/**
 * Obtiene el icono del modal según el tipo de error
 */
export const getErrorModalIcon = (errorCodeOrMessage?: string): 'success' | 'error' | 'warning' | 'info' | 'message' => {
  if (!errorCodeOrMessage) {
    return 'error'
  }

  // Para errores de cuenta bloqueada, mostrar icono de error
  if (errorCodeOrMessage.includes(ERROR_CODES.BLOCKED_ACCOUNT)) {
    return 'error'
  }

  // Para errores de contraseña expirada, mostrar icono de mensaje
  if (errorCodeOrMessage.includes(ERROR_CODES.EXPIRED_PASSWORD)) {
    return 'message'
  }

  // Por defecto, mostrar icono de error
  return 'error'
}